/**
 * Camadas de mapa disponíveis.
 *
 * Uma lista só, consumida pelo seletor e pelo mapa — acrescentar camada é
 * acrescentar item aqui, e mais nada.
 *
 * `id` e nomes de campo em inglês; `name` é o que o usuário lê, em português.
 *
 * > **Sobre as camadas do Google:** `mt1.google.com/vt` não é API licenciada.
 * > Funciona e é largamente usada, mas está fora dos termos de uso do Google — o
 * > caminho oficial é a Maps Tiles API, com chave e cobrança. Fica registrado
 * > aqui para ser decisão consciente, não descoberta.
 */
export interface TileLayerOption {
  readonly id: string;
  /** Texto para o usuário. */
  readonly name: string;
  readonly url: string;
  readonly maxZoom: number;
  readonly attribution: string;
  /** Camada escura: o marcador claro perde contraste sobre imagem de satélite. */
  readonly dark: boolean;
}

export const TILE_LAYERS: readonly TileLayerOption[] = [
  {
    id: "osm",
    name: "OpenStreetMap",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    maxZoom: 19,
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    dark: false,
  },
  {
    id: "googleRoads",
    name: "Google Ruas",
    url: "https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}",
    maxZoom: 20,
    attribution: '&copy; <a href="https://www.google.com/maps">Google Maps</a>',
    dark: false,
  },
  {
    id: "googleSatellite",
    name: "Google Satélite",
    url: "https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
    maxZoom: 20,
    attribution: '&copy; <a href="https://www.google.com/maps">Google Maps</a>',
    dark: true,
  },
  {
    id: "googleHybrid",
    name: "Google Híbrido",
    url: "https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}",
    maxZoom: 20,
    attribution: '&copy; <a href="https://www.google.com/maps">Google Maps</a>',
    dark: true,
  },
  {
    id: "cartoDark",
    name: "Carto Dark",
    url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
    maxZoom: 19,
    attribution: '&copy; <a href="https://carto.com/">CARTO</a>',
    dark: true,
  },
];

/** Padrão: escura, para combinar com o painel e cansar menos em turno longo. */
export const DEFAULT_TILE_LAYER_ID = "cartoDark";

/**
 * Nunca devolve `undefined`: um `id` gravado antes e removido depois cairia num
 * mapa sem camada — tela cinza, sem erro. Cai no padrão.
 */
export function getTileLayer(id: string): TileLayerOption {
  return TILE_LAYERS.find((layer) => layer.id === id) ?? TILE_LAYERS.find((layer) => layer.id === DEFAULT_TILE_LAYER_ID)!;
}
